/** The space's real composite canvas: one stage, every activity, in the customer's own order. */

import {
  ControlSurface,
  controlEntityIndex,
  type PressAction,
  type SelectAction,
} from "@nebulize/overlay/interact";
import type { SelectBounds } from "@nebulize/overlay/interact";
import type { CanvasArrangement, CueFrame, RegionMap, SceneDocument } from "@nebulize/sdk";
import type { CSSProperties, ReactNode, RefObject } from "react";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";

import type { StateEntity } from "@/lib/runtime";
import { submit } from "@/lib/runtime";
import { useCanvasState } from "@/lib/useCanvasState";

import { CanvasPicture } from "../../canvas/CanvasPicture";
import { COMPOSITED, controlLayerOf, type CanvasLayout } from "../../canvas/composition";
import type { AddedActivityRow } from "../types";

import { FULL_CANVAS, type Placement } from "./lib/arrangement";
import { StageClaim } from "./lib/stageClaim";
import { pointIn, readDrag, type ActivityDrag, type StagePoint } from "./lib/drop";

import "./stage.css";

/** What a drop on the stage tells its host: what was dragged, and where, when there is a where. */
export type StageDrop = (drag: ActivityDrag, point: StagePoint | null) => void;

/** What a click on the stage tells its host. One unit for `bounds`: canvas fractions, or absent. */
export interface StageSelection {
  readonly activityId: string;
  readonly canvasKind: string;
  /** What the activity authored, or undefined for the document's own empty space. */
  readonly nodeId: string | undefined;
  /** The drawn instance, unique across repeats. Selection identity. */
  readonly nodeKey: string | undefined;
  /** In canvas fractions. Absent on a flowed canvas, which has no canvas box to be a fraction of. */
  readonly bounds?: SelectBounds;
}

/** The element the interact half measured its pixels against: its own root, inside this box. */
function surfaceRootOf(wrapper: HTMLElement | null): HTMLElement | null {
  const first = wrapper?.firstElementChild;
  return first instanceof HTMLElement ? first : wrapper;
}

/** One control layer's pick, converted from its own pixels into canvas fractions. */
function inCanvasFractions(
  action: SelectAction,
  surface: HTMLElement | null,
  stage: HTMLElement | null,
): StageSelection {
  const held: StageSelection = {
    activityId: action.activityId,
    canvasKind: action.canvasKind,
    nodeId: action.nodeId,
    nodeKey: action.nodeKey,
  };
  if (surface === null || stage === null) return held;

  const hit = surface.getBoundingClientRect();
  const frame = stage.getBoundingClientRect();
  if (frame.width === 0 || frame.height === 0) return held;

  return {
    ...held,
    bounds: {
      x: (hit.left - frame.left + action.bounds.x) / frame.width,
      y: (hit.top - frame.top + action.bounds.y) / frame.height,
      width: action.bounds.width / frame.width,
      height: action.bounds.height / frame.height,
    },
  };
}

/** One mount's live reading, as the composite holds it. */
export interface MountReading {
  scenes: readonly SceneDocument[];
  entities: readonly StateEntity[];
  regions: readonly RegionMap[];
  canvases: readonly string[];
  error: string | null;
}

const NOTHING: MountReading = {
  scenes: [],
  entities: [],
  regions: [],
  canvases: [],
  error: null,
};

/** The box a placement asks for, as a fraction of the stage. */
function boxOf(held: Placement): CSSProperties {
  return {
    left: `${String(held.left * 100)}%`,
    top: `${String(held.top * 100)}%`,
    width: `${String(held.width * 100)}%`,
    height: `${String(held.height * 100)}%`,
  };
}

/** One mount's socket, and its own control layer inside that activity's own box. */
function MountFeed({
  row,
  canvasKind,
  layout,
  place,
  mode,
  stage,
  onReading,
  onCue,
  onSelect,
}: {
  readonly row: AddedActivityRow;
  readonly canvasKind: string;
  readonly layout: CanvasLayout;
  /** Where this activity draws on this canvas, so its controls land there and nowhere else. */
  readonly place: Placement;
  readonly mode: "play" | "select";
  /** The stage box every pick is reported as a fraction of. */
  readonly stage: RefObject<HTMLDivElement | null>;
  readonly onReading: (mountId: string, reading: MountReading) => void;
  /** This mount's cues, for the one composite that draws every mount's documents together. */
  readonly onCue: (frame: CueFrame) => void;
  readonly onSelect: (selection: StageSelection) => void;
}) {
  const mountId = row.mount?.mount_id ?? "";
  const box = useRef<HTMLDivElement>(null);
  const { state, error, frame, cue } = useCanvasState(mountId, undefined, canvasKind);

  const entities = useMemo(() => controlEntityIndex(state?.entities ?? []), [state]);
  const documents = useMemo(
    () => (state?.scenes ?? []).filter((one) => one.canvas_kind === canvasKind),
    [state, canvasKind],
  );

  const reading = useMemo<MountReading>(
    () =>
      state === null
        ? { ...NOTHING, error }
        : {
            scenes: documents,
            entities: state.entities,
            regions: state.regions ?? [],
            canvases: state.canvases ?? [],
            error,
          },
    [state, documents, error],
  );

  useEffect(() => {
    onReading(mountId, reading);
  }, [onReading, mountId, reading]);

  useEffect(() => {
    if (cue !== undefined) onCue(cue);
  }, [onCue, cue]);

  const press = useCallback(
    (action: PressAction) => submit(mountId, action.submission, action.value, action),
    [mountId],
  );

  if (documents.length === 0) return null;
  // Hidden is layout, never lifecycle: the socket above stays open and only the drawing stops.
  if (place.hidden) return null;

  const controls = (
    <ControlSurface
      documents={documents}
      entities={entities}
      canvasKinds={[canvasKind]}
      submit={press}
      layer={controlLayerOf(layout)}
      {...(layout === COMPOSITED ? { className: "cx-canvas-layer-flow" } : {})}
      mode={mode}
      onSelect={(action) => {
        // The interact half measures in its own pixels. One unit leaves this host: fractions.
        onSelect(
          layout === COMPOSITED
            ? inCanvasFractions(action, surfaceRootOf(box.current), stage.current)
            : inCanvasFractions(action, null, null),
        );
      }}
      {...(frame === undefined ? {} : { frame })}
    />
  );

  if (layout === COMPOSITED) {
    return (
      <div className="cx-canvas-layer" style={boxOf(place)} ref={box}>
        {controls}
      </div>
    );
  }

  return (
    <section className="cx-canvas-block" aria-label={row.name}>
      {controls}
      <CanvasPicture
        layout={layout}
        canvasKind={canvasKind}
        scenes={documents}
        entities={entities}
        regions={state?.regions ?? []}
        stale={error !== null}
        {...(cue === undefined ? {} : { cue })}
      />
    </section>
  );
}

/** The arrangement the stage draws with, keyed the way the renderer matches it. */
function arrangementOf(
  rows: readonly AddedActivityRow[],
  canvasKind: string,
  placements: Readonly<Record<string, Placement>>,
): CanvasArrangement {
  const held: Record<string, Placement> = {};
  for (const row of rows) {
    const found = placements[row.added_activity_id];
    if (found !== undefined) held[row.name] = found;
  }
  return { canvases: { [canvasKind]: held } };
}

export interface PlanLayerProps {
  /** The activities drawn on this canvas, back to front. */
  readonly rows: readonly AddedActivityRow[];
  readonly placements: Readonly<Record<string, Placement>>;
  readonly selectedId?: string | null;
  /** Given, every rectangle is a button. Left out, the plan is inert. */
  readonly onSelect?: (addedActivityId: string) => void;
  /** `outlines` is the same plan drawn under a live composite, tinting nothing. */
  readonly variant?: "plan" | "outlines";
}

/** What each activity will draw over, from the arrangement alone. An unarranged one fills the canvas. */
export function PlanLayer({
  rows,
  placements,
  selectedId = null,
  onSelect,
  variant = "plan",
}: PlanLayerProps) {
  return (
    <div className={variant === "outlines" ? "cx-plan cx-plan-outlines" : "cx-plan"}>
      {rows.map((row) => {
        const held = placements[row.added_activity_id] ?? FULL_CANVAS;
        if (held.hidden) return null;
        const chosen = selectedId === row.added_activity_id;
        const className = chosen ? "cx-plan-box cx-plan-box-on" : "cx-plan-box";
        const style = { ...boxOf(held), zIndex: held.layer };
        const name = <span className="cx-plan-name">{row.name}</span>;

        if (onSelect === undefined) {
          return (
            <div key={row.added_activity_id} className={className} style={style} aria-hidden="true">
              {name}
            </div>
          );
        }

        return (
          <button
            key={row.added_activity_id}
            type="button"
            className={className}
            style={style}
            aria-pressed={chosen}
            aria-label={row.name}
            onClick={() => {
              onSelect(row.added_activity_id);
            }}
          >
            {name}
          </button>
        );
      })}
    </div>
  );
}

/** The aspect a stage keeps, when a host has one to give. */
function aspectStyle(aspect: string | undefined): { style?: CSSProperties } {
  if (aspect === undefined) return {};
  return { style: { "--cx-stage-aspect": aspect } as CSSProperties };
}

/** The one framed box every stage draws in, and the one drop target the editor has. */
function StageFrame({
  aspect,
  layout,
  frame,
  picked,
  onEmpty,
  onDrop,
  children,
}: {
  readonly aspect?: string;
  readonly layout: CanvasLayout;
  readonly frame: RefObject<HTMLDivElement | null>;
  /** Set by whichever half reported a pick during this click, and cleared as the click starts. */
  readonly picked: RefObject<boolean>;
  /** A click that reached no document at all, which is the space being selected. */
  readonly onEmpty?: () => void;
  readonly onDrop?: StageDrop;
  readonly children: ReactNode;
}) {
  const claim = useCallback(() => {
    picked.current = true;
  }, [picked]);

  return (
    <div
      className={
        layout === COMPOSITED ? "cx-stage-frame cx-stage-aspect" : "cx-stage-frame cx-stage-flowed"
      }
      ref={frame}
      onPointerDownCapture={() => {
        picked.current = false;
      }}
      onClick={
        onEmpty === undefined
          ? undefined
          : () => {
              if (!picked.current) onEmpty();
            }
      }
      {...aspectStyle(aspect)}
      onDragOver={
        onDrop === undefined
          ? undefined
          : (event) => {
              if (readDrag(event.dataTransfer) === null) return;
              event.preventDefault();
            }
      }
      onDrop={
        onDrop === undefined
          ? undefined
          : (event) => {
              const drag = readDrag(event.dataTransfer);
              if (drag === null) return;
              event.preventDefault();
              // A flow has no coordinate, so a drop there orders and never places.
              const box = frame.current?.getBoundingClientRect();
              const point =
                layout !== COMPOSITED || box === undefined
                  ? null
                  : pointIn(box, event.clientX, event.clientY);
              onDrop(drag, point);
            }
      }
    >
      <StageClaim.Provider value={claim}>{children}</StageClaim.Provider>
    </div>
  );
}

export interface PlanStageProps {
  /** The activities drawn on this canvas, back to front. */
  readonly rows: readonly AddedActivityRow[];
  readonly placements: Readonly<Record<string, Placement>>;
  readonly selectedId?: string | null;
  readonly onSelect?: (addedActivityId: string) => void;
  /** A css `aspect-ratio`. Nothing declares one yet, so a stage stays 16/9 until something does. */
  readonly aspect?: string;
  /** Drawn over the plan: the arrange layer, when the host is in the move tool. */
  readonly overlay?: ReactNode;
  readonly layout?: CanvasLayout;
  readonly onDropActivity?: StageDrop;
  /** A click that reached no rectangle at all, which is the space being selected. */
  readonly onSelectNothing?: () => void;
}

/** The same stage as the composite, drawing the plan instead of a mount. Nothing need be running. */
export function PlanStage({
  rows,
  placements,
  selectedId = null,
  onSelect,
  aspect,
  overlay,
  layout = COMPOSITED,
  onDropActivity,
  onSelectNothing,
}: PlanStageProps) {
  const frame = useRef<HTMLDivElement>(null);
  const picked = useRef(false);

  return (
    <StageFrame
      layout={layout}
      frame={frame}
      picked={picked}
      {...(aspect === undefined ? {} : { aspect })}
      {...(onSelectNothing === undefined ? {} : { onEmpty: onSelectNothing })}
      {...(onDropActivity === undefined ? {} : { onDrop: onDropActivity })}
    >
      {layout === COMPOSITED ? (
        <PlanLayer
          rows={rows}
          placements={placements}
          selectedId={selectedId}
          {...(onSelect === undefined
            ? {}
            : {
                onSelect: (addedActivityId: string) => {
                  picked.current = true;
                  onSelect(addedActivityId);
                },
              })}
        />
      ) : (
        <FlowedPlan rows={rows} placements={placements} />
      )}
      {layout === COMPOSITED ? overlay : null}
    </StageFrame>
  );
}

/** What a flowed canvas will draw before anything runs: the order, because there is no rectangle. */
function FlowedPlan({
  rows,
  placements,
}: {
  readonly rows: readonly AddedActivityRow[];
  readonly placements: Readonly<Record<string, Placement>>;
}) {
  return (
    <div className="cx-flow-stage">
      {rows.map((row) => {
        const held = placements[row.added_activity_id] ?? FULL_CANVAS;
        if (held.hidden) return null;
        return (
          <div className="cx-flow-plan" key={row.added_activity_id}>
            {row.name}
          </div>
        );
      })}
    </div>
  );
}

export interface SpaceCanvasProps {
  /** The live mounts drawn on this canvas, in the order the space lists them. */
  readonly rows: readonly AddedActivityRow[];
  readonly canvasKind: string;
  readonly placements: Readonly<Record<string, Placement>>;
  readonly mode: "play" | "select";
  readonly onSelect: (selection: StageSelection) => void;
  /** A css `aspect-ratio`. Nothing declares one yet, so a stage stays 16/9 until something does. */
  readonly aspect?: string;
  /** Drawn over the stage: selection outlines and drag handles. */
  readonly overlay?: ReactNode;
  /** How this canvas draws, from its own declaration. */
  readonly layout: CanvasLayout;
  readonly onDropActivity?: StageDrop;
  /** A click that reached no document at all, which is the space being selected. */
  readonly onSelectNothing?: () => void;
}

/** Every activity on one canvas, drawn as the running canvas draws it. */
export function SpaceCanvas({
  rows,
  canvasKind,
  placements,
  mode,
  onSelect,
  aspect,
  overlay,
  layout,
  onDropActivity,
  onSelectNothing,
}: SpaceCanvasProps) {
  const [readings, setReadings] = useState<Record<string, MountReading>>({});
  /** The last cue any mount on this canvas fired. The render half plays each envelope once. */
  const [cue, setCue] = useState<CueFrame | undefined>(undefined);
  const frame = useRef<HTMLDivElement>(null);
  const picked = useRef(false);

  /** Every pick from either half goes through here, so an empty click is the one that did not. */
  const reportSelection = useCallback(
    (selection: StageSelection) => {
      picked.current = true;
      onSelect(selection);
    },
    [onSelect],
  );

  const report = useCallback((mountId: string, reading: MountReading) => {
    setReadings((held) => (held[mountId] === reading ? held : { ...held, [mountId]: reading }));
  }, []);

  const takeCue = useCallback((fired: CueFrame) => {
    setCue(fired);
  }, []);

  const live = useMemo(() => rows.filter((row) => row.mount?.mount_id != null), [rows]);

  const scenes = useMemo(
    () => live.flatMap((row) => readings[row.mount?.mount_id ?? ""]?.scenes ?? []),
    [live, readings],
  );
  const entities = useMemo(
    () =>
      controlEntityIndex(
        live.flatMap((row) => readings[row.mount?.mount_id ?? ""]?.entities ?? []),
      ),
    [live, readings],
  );
  const regions = useMemo(
    () => live.flatMap((row) => readings[row.mount?.mount_id ?? ""]?.regions ?? []),
    [live, readings],
  );
  const stale = live.some((row) => readings[row.mount?.mount_id ?? ""]?.error != null);

  const arrangement = useMemo(
    () => arrangementOf(rows, canvasKind, placements),
    [rows, canvasKind, placements],
  );

  const composited = layout === COMPOSITED;

  const feeds = live.map((row) => (
    <MountFeed
      key={row.mount?.mount_id ?? row.added_activity_id}
      row={row}
      canvasKind={canvasKind}
      layout={layout}
      place={placements[row.added_activity_id] ?? FULL_CANVAS}
      mode={mode}
      stage={frame}
      onReading={report}
      onCue={takeCue}
      onSelect={reportSelection}
    />
  ));

  return (
    <StageFrame
      layout={layout}
      frame={frame}
      picked={picked}
      {...(aspect === undefined ? {} : { aspect })}
      {...(onSelectNothing === undefined ? {} : { onEmpty: onSelectNothing })}
      {...(onDropActivity === undefined ? {} : { onDrop: onDropActivity })}
    >
      {composited ? <PlanLayer rows={rows} placements={placements} variant="outlines" /> : null}
      {composited ? (
        <CanvasPicture
          layout={layout}
          canvasKind={canvasKind}
          scenes={scenes}
          entities={entities}
          regions={regions}
          arrangement={arrangement}
          stale={stale}
          {...(cue === undefined ? {} : { cue })}
          onPick={(hit) => {
            // Already canvas fractions, which is the unit this host reports in.
            reportSelection({
              activityId: hit.activityId,
              canvasKind: hit.canvasKind,
              nodeId: hit.nodeId,
              nodeKey: hit.nodeKey,
              bounds: {
                x: hit.box.left,
                y: hit.box.top,
                width: hit.box.width,
                height: hit.box.height,
              },
            });
          }}
        />
      ) : null}
      {composited ? feeds : <div className="cx-flow-stage">{feeds}</div>}
      {composited ? overlay : null}
    </StageFrame>
  );
}
